
import { useState, useRef, useCallback } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import CONFIG from "../../../config";
import { generateExcelFromTable } from "../utils/excelGenerator";
import { formatDataForTable } from "../utils/dataFormatter";
import { getColumnLetter } from "../utils/tableHelpers";

export const useExcelOperations = ({
  statementId,
  companyName,
  activeStatement,
  rows,
  columns,
  setEditHistory,
  showNotification,
  setRows,
  setColumns,
  onDataUpdate
}) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [isDownloadingCurrent, setIsDownloadingCurrent] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadMessage, setUploadMessage] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isRefreshingData, setIsRefreshingData] = useState(false);
  
  const fileInputRef = useRef(null);

  const handleDownloadOriginalExcel = useCallback(async () => {
    if (!statementId) {
      showNotification('No statement ID available for download', 'error');
      return;
    }
    
    setIsDownloading(true);
    
    try {
      const downloadUrl = CONFIG.getEndpoint(CONFIG.API.EXCEL.STATEMENT_DOWNLOAD, true, statementId);
      const headers = CONFIG.getAuthHeaders();
      
      const response = await axios.get(downloadUrl, {
        headers,
        responseType: 'blob'
      });
      
      // Get filename from headers or generate
      const safeCompanyName = companyName.replace(/[^a-z0-9]/gi, '_').toLowerCase();
      const safeStatement = activeStatement.replace(/\s+/g, '_').toLowerCase();
      let filename = `${safeCompanyName}_${safeStatement}_original.xlsx`;
      
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        const matches = contentDisposition.match(/filename="(.+)"/);
        if (matches?.[1]) {
          filename = matches[1];
        } else if (contentDisposition.match(/filename\*=UTF-8''(.+)/)) {
          const utf8Match = contentDisposition.match(/filename\*=UTF-8''(.+)/);
          if (utf8Match && utf8Match[1]) {
            filename = decodeURIComponent(utf8Match[1]);
          }
        }
      }
      
      // Create blob and trigger download
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      // Add to history
      setEditHistory(prev => [...prev, {
        id: Date.now(),
        type: 'download_original_excel',
        filename,
        timestamp: new Date().toISOString(),
        statementType: activeStatement,
        companyName,
        source: 'server_file'
      }]);
      
      showNotification(`Downloaded original Excel: ${filename}`, 'success');
      
    } catch (error) {
      console.error('Download error:', error);
      
      // Fallback: generate from current table data
      if (rows.length > 0) {
        showNotification('Server download failed, generating from current table...', 'warning');
        setTimeout(handleDownloadCurrentTable, 500);
      } else {
        showNotification(`Failed to download Excel file: ${error.message}`, 'error');
      }
    } finally {
      setIsDownloading(false);
    }
  }, [statementId, companyName, activeStatement, showNotification, setEditHistory, rows.length]);

  const handleDownloadCurrentTable = useCallback(async () => {
    if (rows.length === 0) {
      showNotification('No table data to download', 'error');
      return;
    }
    
    setIsDownloadingCurrent(true);
    
    try {
      const wb = generateExcelFromTable(rows, columns, activeStatement, companyName);
      if (!wb) {
        throw new Error('Failed to generate Excel');
      }
      
      const safeCompanyName = companyName.replace(/[^a-z0-9]/gi, '_').toLowerCase();
      const safeStatement = activeStatement.replace(/\s+/g, '_').toLowerCase();
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').split('T')[0];
      const filename = `${safeCompanyName}_${safeStatement}_current_${timestamp}.xlsx`;
      
      XLSX.writeFile(wb, filename);
      
      setEditHistory(prev => [...prev, {
        id: Date.now(),
        type: 'download_current_table',
        filename,
        timestamp: new Date().toISOString(),
        statementType: activeStatement,
        companyName,
        rowCount: rows.length,
        columnCount: columns.length,
        source: 'current_table_data'
      }]);
      
      showNotification(`Downloaded current table as ${filename}`, 'success');
      
    } catch (error) {
      console.error('Error downloading current table:', error);
      showNotification(`Failed to download current table: ${error.message}`, 'error');
    } finally {
      setIsDownloadingCurrent(false);
    }
  }, [rows, columns, activeStatement, companyName, showNotification, setEditHistory]);

  const handleUploadClick = useCallback(() => {
    if (!statementId) {
      showNotification('No statement selected for upload', 'error');
      return;
    }
    
    // Reset states
    setUploadError(null);
    setUploadSuccess(false);
    setUploadMessage("");
    setUploadProgress(0);
    
    // Check if file input ref exists
    if (!fileInputRef.current) {
      showNotification('Upload failed: Cannot access file input', 'error');
      return;
    }
    
    // Trigger file input click
    fileInputRef.current.click();
  }, [statementId, showNotification]);

  const handleFileUpload = useCallback(async (event) => {
    const file = event.target.files[0];
    
    if (!file) {
      return;
    }
    
    // Reset states
    setUploadError(null);
    setUploadSuccess(false);
    setUploadMessage("");
    
    // Validate file
    if (!file.name.toLowerCase().endsWith('.xlsx') && !file.name.toLowerCase().endsWith('.xls')) {
      const errorMsg = 'Please upload an Excel file (.xlsx or .xls)';
      setUploadError(errorMsg);
      showNotification(errorMsg, 'error');
      return;
    }
    
    if (file.size > 50 * 1024 * 1024) {
      const errorMsg = 'File size exceeds 50MB limit';
      setUploadError(errorMsg);
      showNotification(errorMsg, 'error');
      return;
    }
    
    setIsUploading(true);
    setUploadProgress(10);
    
    try {
      // Upload the file
      setUploadProgress(30);
      const formData = new FormData();
      formData.append('file', file);
      formData.append('overwrite', 'true');
      
      // Get the upload URL
      const uploadUrl = CONFIG.getEndpoint(CONFIG.API.EXCEL.UPLOAD_UPDATE, true, statementId);
      
      const response = await axios.post(uploadUrl, formData, {
        headers: CONFIG.getAuthHeadersForFiles(),
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(30 + (percentCompleted * 0.6));
          }
        },
        timeout: 300000 // 5 minutes timeout for large files
      });
      
      setUploadProgress(100);
      
      if (response.data?.success) {
        const successMsg = 'Excel file uploaded and updated successfully!';
        setUploadSuccess(true);
        setUploadMessage(successMsg);
        showNotification(successMsg, 'success');
        
        // Add to upload history
        setEditHistory(prev => [...prev, {
          id: Date.now(),
          type: 'upload_excel',
          filename: file.name,
          timestamp: new Date().toISOString(),
          statementType: activeStatement,
          fileSize: file.size,
          source: 'user_upload'
        }]);
        
        // Refresh table data after a short delay
        setTimeout(() => {
          refreshTableDataImmediately();
        }, 1000);
        
        // Clear success message after 5 seconds
        setTimeout(() => {
          setUploadSuccess(false);
          setUploadMessage("");
        }, 5000);
        
      } else {
        throw new Error(response.data?.message || 'Upload failed without error message');
      }
      
    } catch (error) {
      let errorMessage = error.message;
      
      if (error.response) {
        // Server responded with error
        if (error.response.status === 401) {
          errorMessage = 'Authentication failed. Please login again.';
        } else if (error.response.status === 403) {
          errorMessage = 'Permission denied. You may not have upload rights for this statement.';
        } else if (error.response.status === 404) {
          errorMessage = 'Upload endpoint not found. Please contact support.';
        } else if (error.response.status === 413) {
          errorMessage = 'File too large for server.';
        } else if (error.response.status >= 500) {
          errorMessage = 'Server error. Please try again later.';
        } else if (error.response.data?.detail) {
          errorMessage = error.response.data.detail;
        }
      } else if (error.request) {
        // No response received
        errorMessage = 'No response from server. Please check your connection.';
      } else if (error.message.includes('Network Error')) {
        errorMessage = 'Network error. Please check your connection and try again.';
      } else if (error.message.includes('timeout')) {
        errorMessage = 'Upload timeout. Please try again with a smaller file.';
      }
      
      setUploadError(errorMessage);
      showNotification(`Upload failed: ${errorMessage}`, 'error');
    } finally {
      setIsUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      // Reset progress after delay
      setTimeout(() => setUploadProgress(0), 2000);
    }
  }, [statementId, activeStatement, showNotification, setEditHistory]);

  const refreshTableDataImmediately = useCallback(async () => {
    if (!statementId) {
      showNotification('No statement ID for refresh', 'error');
      return;
    }
    
    setIsRefreshingData(true);
    
    try {
      // Clear existing data first
      setRows([]);
      setColumns([]);
      
      // Fetch fresh data from server
      const dataUrl = CONFIG.getEndpoint(CONFIG.API.EXCEL.STATEMENT_DATA, true, statementId);
      
      const response = await axios.get(dataUrl, {
        headers: CONFIG.getAuthHeaders(),
        timeout: 10000
      });
      
      if (response.data.success && response.data.data) {
        const jsonData = response.data.data;
        const formattedData = formatDataForTable(jsonData, companyName, activeStatement);
        
        if (formattedData && formattedData.length > 0) {
          setRows(formattedData);
          onDataUpdate?.(formattedData);
          
          // Update columns based on new data
          if (formattedData.length > 0) {
            const headers = Object.keys(formattedData[0]);
            const newColumns = headers.map((header, index) => {
              let width = 120;
              let label = header;
              
              const yearMatch = header.match(/\b(19|20)\d{2}\b/);
              if (yearMatch) {
                label = `FY ${yearMatch[0]}`;
              } else if (index === 0) {
                label = "Particular";
                width = 180;
              }
              
              return {
                id: header,
                label: label,
                width: width
              };
            });
            
            setColumns(newColumns);
          }
          
          showNotification('Table refreshed with uploaded file content', 'success');
        } else {
          showNotification('No data returned from server', 'warning');
        }
      } else {
        showNotification('Failed to fetch updated data', 'error');
      }
    } catch (error) {
      console.error('Error refreshing table after upload:', error);
      showNotification('Failed to refresh table. Please reload the page.', 'error');
      
      // Try to reload after delay
      setTimeout(() => {
        if (statementId) {
          refreshTableDataImmediately();
        }
      }, 3000);
    } finally {
      setIsRefreshingData(false);
    }
  }, [statementId, companyName, activeStatement, showNotification, setRows, setColumns, onDataUpdate]);

  return {
    // State
    isDownloading,
    isDownloadingCurrent,
    isUploading,
    uploadError,
    uploadSuccess,
    uploadMessage,
    uploadProgress,
    isRefreshingData,
    
    // Refs
    fileInputRef,
    
    // Actions
    setUploadError,
    setIsRefreshingData,
    
    // Handlers
    handleDownloadOriginalExcel,
    handleDownloadCurrentTable,
    handleUploadClick,
    handleFileUpload,
    refreshTableDataImmediately
  };
};
